import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Shield, Globe2, Sparkles, MapPin } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { IMAGES } from "../lib/site";

const BENGALURU = {
  id: "bengaluru",
  city: "Bengaluru, India",
  img: IMAGES.bengaluru,
  overview:
    "Bengaluru remains India's most resilient growth corridor — IT capital, deep talent pipeline, and a real estate market that has comfortably outpaced inflation for a decade. The city now offers both end-user homes and serious capital appreciation plays.",
  stats: [
    { v: "₹1.42L Cr", l: "Annual residential sales (2025)" },
    { v: "12–18%", l: "CAGR in premium micro-markets" },
    { v: "3.4%", l: "Average gross rental yield" },
    { v: "80+", l: "Developer relationships" },
  ],
  microMarkets: [
    { n: "Whitefield", o: "Established IT hub with mature schools, retail and metro access." },
    { n: "Sarjapur Road", o: "Premium gated communities with strong end-user demand." },
    { n: "North Bengaluru", o: "Airport corridor — long-runway appreciation play." },
    { n: "Hebbal", o: "Connectivity sweet-spot between CBD and airport." },
    { n: "Devanahalli", o: "Early-stage entry with multi-decade upside." },
    { n: "Indiranagar / Koramangala", o: "Boutique luxury homes for HNI end-users." },
  ],
  why: [
    "Diversified employment base — IT, biotech, deep-tech and global capability centres",
    "Metro Phase 3 unlocking new corridors before pricing catches up",
    "Strong rental demand from a young, mobile workforce",
    "Mature regulatory environment with RERA-led transparency",
  ],
};

const DUBAI = {
  id: "dubai",
  city: "Dubai, UAE",
  img: IMAGES.dubaiAerial,
  overview:
    "Dubai is one of the world's most investor-friendly real estate markets — 0% capital gains tax, residency pathways, and yields that comfortably outperform most global cities. It's the natural diversifier for Indian capital.",
  stats: [
    { v: "AED 683B", l: "Transactions in 2025" },
    { v: "6–9%", l: "Gross rental yields" },
    { v: "0%", l: "Capital gains tax" },
    { v: "AED 2M+", l: "Golden Visa threshold" },
  ],
  microMarkets: [
    { n: "Dubai Marina", o: "Iconic waterfront yields and strong short-let demand." },
    { n: "Downtown Dubai", o: "Trophy district — Burj Khalifa, Opera, premium retail." },
    { n: "Business Bay", o: "Mid-luxury living with corporate-tenant demand." },
    { n: "Palm Jumeirah", o: "Ultra-prime — limited supply, durable value." },
    { n: "Dubai Hills Estate", o: "Family-led suburb with strong appreciation." },
    { n: "JVC & Arjan", o: "Affordable entry with healthy rental yields." },
  ],
  why: [
    "Residency via property investment (Golden Visa from AED 2M)",
    "0% income tax on rental yields for individuals",
    "Strong AED–USD peg providing currency stability for global investors",
    "Transparent freehold ownership for non-residents",
  ],
};

const CityBlock = ({ data }) => (
  <section id={data.id} data-testid={`market-section-${data.id}`} className="py-24 lg:py-32 border-b border-black/5 scroll-mt-24">
    <div className="container-tight">
      <Reveal>
        <div className="relative rounded-2xl overflow-hidden">
          <img src={data.img} alt={data.city} loading="lazy" className="w-full h-[420px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-navy/80 via-navy/30 to-transparent" />
          <div className="absolute inset-0 p-10 lg:p-14 flex flex-col justify-end text-white">
            <span className="eyebrow text-gold flex items-center gap-2"><MapPin size={12} /> Market focus</span>
            <h2 className="mt-3 font-serif text-5xl sm:text-6xl">{data.city}</h2>
          </div>
        </div>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          <h3 className="font-serif text-3xl text-navy">Market overview</h3>
          <p className="mt-5 text-navy/70 text-lg leading-relaxed">{data.overview}</p>

          <h4 className="mt-12 font-serif text-2xl text-navy">Top micro-markets</h4>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {data.microMarkets.map((m) => (
              <div key={m.n} className="rounded-xl border border-black/5 bg-white p-5 hover:border-gold/40 transition-colors">
                <div className="font-serif text-xl text-navy">{m.n}</div>
                <div className="mt-1.5 text-sm text-navy/65 leading-relaxed">{m.o}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-5 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {data.stats.map((s, i) => (
              <div key={i} data-testid={`market-stat-${data.id}-${i}`} className="rounded-2xl bg-[#FAFAFA] p-6 border border-black/5">
                <div className="font-serif text-3xl text-navy">{s.v}</div>
                <div className="mt-2 text-xs uppercase tracking-wider text-navy/55">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl bg-navy text-white p-7">
            <div className="eyebrow text-gold">Why invest here</div>
            <ul className="mt-4 space-y-3 text-sm text-white/85">
              {data.why.map((w) => (
                <li key={w} className="flex items-start gap-3">
                  <Sparkles size={14} className="text-gold mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn-gold mt-7 text-sm w-full">
              Get a tailored thesis <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Markets = () => (
  <>
    <Seo
      title="Bengaluru & Dubai Real Estate Markets — Insights & Micro-markets"
      path="/markets"
      description="Deep market intelligence on Bengaluru (Whitefield, Sarjapur, North Bengaluru) and Dubai (Marina, Downtown, Business Bay) with stats, yields, and micro-market analysis."
    />
    <section className="relative bg-navy text-white py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src={IMAGES.heroDubai} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy" />
      </div>
      <div className="container-tight relative z-10">
        <Reveal>
          <span className="eyebrow text-gold">Markets</span>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl lg:text-7xl max-w-3xl leading-[1.05]">
            Two cities. <span className="text-gold">Deeply known.</span>
          </h1>
          <p className="mt-7 text-white/75 max-w-2xl text-lg">
            We don't dabble — we live in these markets. Here's how we read them today.
          </p>
        </Reveal>
        <div className="mt-12 flex flex-wrap gap-4">
          <a href="#bengaluru" data-testid="market-jump-bengaluru" className="btn-gold text-sm"><TrendingUp size={14}/> Bengaluru</a>
          <a href="#dubai" data-testid="market-jump-dubai" className="btn-ghost-navy text-sm" style={{borderColor:"rgba(255,255,255,0.25)", color:"#fff"}}><Globe2 size={14}/> Dubai</a>
        </div>
      </div>
    </section>

    <CityBlock data={BENGALURU} />
    <CityBlock data={DUBAI} />

    <section className="bg-[#FAFAFA] py-20">
      <div className="container-tight text-center">
        <Reveal>
          <Shield className="mx-auto text-gold" size={28} strokeWidth={1.5} />
          <h3 className="mt-4 font-serif text-3xl text-navy max-w-xl mx-auto">A tailored thesis beats a generic one — every time.</h3>
          <Link to="/contact" className="btn-navy mt-7 text-sm">
            Request a market memo <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default Markets;
